/**
 * Compare — session memory.
 *
 * The Compare page unmounts when the viewer jumps to an account in the
 * Workspace. This keeps (in memory, per property + year) the view they built
 * — columns, baseline, period, blend, open rows, scroll — and the jump they
 * made, so "Back to Compare" lands them where they were.
 */

import { useSyncExternalStore } from 'react';
import type { SeriesId } from './compareData';
import type { MonthRange } from './MonthRangeRail';

export interface CompareViewState {
  active: SeriesId[];
  baseId: SeriesId;
  showVariance: boolean;
  blendActuals: boolean;
  range: MonthRange;
  collapsedBands: string[];
  expandedDepts: string[];
  scrollTop: number;
}

export interface CompareReturn {
  /** Row the viewer jumped from — highlighted on the way back. */
  rowKey: string;
  accountLabel: string;
  /** Working version at the time of the jump, restored if the jump switched it. */
  versionId: number | null;
}

const views = new Map<string, CompareViewState>();
let returnTo: CompareReturn | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export const compareScopeKey = (orgId: number | null, locationId: number | null, year: number) => `${orgId}:${locationId}:${year}`;

export const loadCompareView = (scope: string): CompareViewState | undefined => views.get(scope);
export const saveCompareView = (scope: string, view: CompareViewState) => { views.set(scope, view); };

export const setCompareReturn = (next: CompareReturn | null) => { returnTo = next; emit(); };
export const getCompareReturn = () => returnTo;

/** The pending "back to Compare" target, if the viewer jumped out of it. */
export function useCompareReturn(): CompareReturn | null {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
    getCompareReturn
  );
}
